import type { RJSFSchema, WidgetProps } from '@rjsf/utils';
import { useId } from 'react';
import styled, { css, useTheme } from 'styled-components';
import type { ITheme } from '../../../utils/theme';
import { TypographyVariant } from '../../../utils/theme';
import type { IconTileProps } from '../../icon-tile/icon-tile';
import IconTile from '../../icon-tile/icon-tile';
import InputFeedback from '../../input-feedback';
import RadioButton from '../../radio-button/radio-button';
import Typography from '../../typography/typography';

interface RadioWidgetProps extends Omit<WidgetProps, 'value'> {
  value?: string | boolean;
  hasError?: boolean;
}

const RadioWidget = ({
  label,
  value,
  onChange,
  disabled,
  readonly,
  schema,
  uiSchema,
  hasError,
  rawErrors,
}: RadioWidgetProps) => {
  const theme = useTheme();
  const titleId = useId();
  const buttonsContainerId = useId();
  const options = schema.oneOf as RJSFSchema[];
  const iconTiles = uiSchema?.['iconTiles'] as Partial<IconTileProps>[];
  const showError = hasError || rawErrors?.length > 0;

  return (
    <>
      {label && (
        <TypographyContainer>
          <Typography
            variant={TypographyVariant.bodySm2}
            id={titleId}
            color={
              showError ? theme.palette.error.main : theme.palette.neutral[100]
            }
          >
            {label}
          </Typography>
          {schema?.description && (
            <Typography
              variant={TypographyVariant.bodySm1}
              color={
                showError ? theme.palette.error.main : theme.palette.neutral[90]
              }
            >
              {schema?.description}
            </Typography>
          )}
        </TypographyContainer>
      )}

      <ButtonsContainer
        role="radiogroup"
        id={buttonsContainerId}
        aria-labelledby={titleId}
        $isIconTiles={!!iconTiles}
      >
        {iconTiles
          ? iconTiles?.map((option, index) => (
              <IconTile
                key={options[index].title}
                label={options[index].title ?? ''}
                description={option.description ?? ''}
                icon={option.icon}
                isSelected={value === options[index].const}
                onClick={(event) => {
                  event.preventDefault();
                  onChange(options[index].const);
                }}
                disabled={disabled || readonly}
              />
            ))
          : options?.map((option) => (
              <RadioButton
                key={option.title}
                label={option.title ?? ''}
                checked={value === option.const}
                onChange={() => onChange(option.const)}
                disabled={disabled || readonly}
                hasError={showError}
                typographyVariant={TypographyVariant.bodyMd1}
              />
            ))}
      </ButtonsContainer>
      <InputFeedback
        errorMessage={showError ? rawErrors?.join(', ') : undefined}
        id={`${buttonsContainerId}-error`}
      />
    </>
  );
};

export default RadioWidget;

const ButtonsContainer = styled.div`
  ${({ theme, $isIconTiles }: { theme: ITheme; $isIconTiles: boolean }) => css`
    display: flex;
    flex-direction: ${$isIconTiles ? 'row' : 'column'};
    gap: ${theme.spacing($isIconTiles ? 2 : 1.5)};
  `}
`;

const TypographyContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(0.5)};
    margin-bottom: ${theme.spacing(1)};
  `}
`;
